






































































































































































































































































































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { Validations } from "vuelidate-property-decorators";
import { required } from "vuelidate/lib/validators";
import { Address } from "@/models/address/address";
import {
  ShippingAddressType,
  DeliveryServices
} from "@/models/order/helpers";
import { NetSuiteCustomerAddress } from "@/models/netsuite/netsuite";
import FormValidation from "@/components/elements/form/FormValidation.vue";
import countries from "@/models/form/data";
import states from "@/models/form/state-abbrv";
import provinces from "@/models/form/prov-abbrv";
import { EventBus } from "@/event-bus";

@Component({
  components: {
    FormValidation
  }
})
export default class Shipping extends Vue {
  protected countries = countries;
  protected country = "";
  protected selectedAddressId: number | "new" | null = null;

  // Freight estimate fields
  protected freightEstimate: boolean | null = null;
  protected freightAddressType: ShippingAddressType | "" = "";
  protected freightDeliveryService: DeliveryServices | "" = "";
  protected submitted = false;
  protected shippingAddressTypeOptions: string[] = Object.values(
    ShippingAddressType
  );

  @Prop() label!: string;
  @Prop() addressee!: string;
  @Prop() attention!: string;
  @Prop() address!: Address;
  @Prop({ default: () => [] }) addresses!: NetSuiteCustomerAddress[];
  @Prop({ default: true }) isNewCustomer!: boolean;
  @Prop({ default: "" }) freight_instructions!: string;
  @Prop({ default: null }) request_freight_estimate!: boolean | null;

  @Validations()
  validations() {
    const freightRequired = this.freightEstimate === true;
    // The address inputs are only rendered for manual entry. When a saved
    // NetSuite address (e.g. PICKUP) is selected, those fields are hidden, so
    // requiring them here blocks REVIEW QUOTE with an invisible, unfixable
    // error whenever the saved address has a sparse field. Trust saved
    // addresses and only enforce the required rules on the visible inputs.
    const manualEntry =
      this.selectedAddressId === "new" || this.addresses.length === 0;
    return {
      label: {},
      addressee: {},
      address: {
        address_1: manualEntry ? { required } : {},
        city: manualEntry ? { required } : {},
        state: {
          stateReq: value => {
            if (!manualEntry) {
              return true;
            }
            if (
              !value &&
              (this.address.country === "US" ||
                this.address.country === "Canada")
            ) {
              return false;
            } else {
              return true;
            }
          }
        },
        postal_code: manualEntry ? { required } : {},
        country: manualEntry ? { required } : {}
      },
      freightEstimate: { required },
      freightAddressType: { required },
      freightDeliveryService: freightRequired ? { required } : {}
    };
  }

  @Watch("$v.$invalid", { deep: true })
  onFormChange(): void {
    if (this.submitted) {
      this.validateForm();
    }
  }

  @Watch("country")
  public updateCountry() {
    const newAddress = Object.assign(
      {},
      { ...this.address },
      { country: this.country }
    );
    this.$emit(`update:address`, newAddress);
    if (this.submitted && this.$v.address["country"]) {
      this.$v.address["country"]!.$touch();
    }
    if (this.country !== "US" && this.country !== "Canada") {
      this.updateAddress({ state: "" }, "state");
    }
  }

  created() {
    EventBus.$on("submitCheckoutForm", this.validateForm);
    this.country = this.address.country ? this.address.country : "US";
    // If addresses are already loaded (e.g. returning from review), restore the
    // selected radio. Usually they are NOT: the customer record is fetched
    // asynchronously, so this component is created with `addresses` still empty
    // and the radio has to be restored later by the addresses watcher.
    const match = this.matchSavedAddress(this.addresses);
    this.selectedAddressId = match ? match.address_id : "new";
    // Restore freight estimate state from prop if returning from review.
    // request_freight_estimate is the source of truth for the Yes/No answer:
    // it survives on its own, whereas address type and delivery service can be
    // re-derived from the selected address (applySelectedAddress) but the Yes/No
    // answer cannot — which is why it used to blank out on a failed-submit round
    // trip. Fall back to parsing the string for older saved data.
    if (
      this.request_freight_estimate === true ||
      this.request_freight_estimate === false
    ) {
      this.freightEstimate = this.request_freight_estimate;
    }
    if (this.freight_instructions) {
      const addrMatch = this.freight_instructions.match(
        /Address Type - ([^.]+)\./
      );
      const freightMatch = this.freight_instructions.match(
        /Freight Estimate - ([^.]+)\./
      );
      const svcMatch = this.freight_instructions.match(/Services - ([^.]+)\./);
      this.freightAddressType = (addrMatch?.[1] ?? "") as
        | ShippingAddressType
        | "";
      if (
        this.request_freight_estimate !== true &&
        this.request_freight_estimate !== false
      ) {
        this.freightEstimate = (freightMatch?.[1] ?? "Yes") === "Yes";
      }
      this.freightDeliveryService = (svcMatch?.[1] ?? "") as
        | DeliveryServices
        | "";
    }
  }

  /**
   * The customer's saved address that the current shipping values came from, if
   * any. Matched on street + postal code because that is all that survives the
   * round trip — the address is stored on the quote as a flat copy, with no
   * reference back to the NetSuite address it was chosen from.
   */
  private matchSavedAddress(
    addresses: NetSuiteCustomerAddress[]
  ): NetSuiteCustomerAddress | undefined {
    const addr1 = (this.address?.address_1 ?? "").trim().toLowerCase();
    if (!addr1) return undefined;
    const zip = (this.address?.postal_code ?? "").trim();
    return addresses.find(
      a =>
        (a.addr1 ?? "").trim().toLowerCase() === addr1 &&
        (a.zip ?? "").trim() === zip
    );
  }

  @Watch("addresses")
  onAddressesLoaded(addresses: NetSuiteCustomerAddress[]): void {
    if (!addresses.length) return;
    // This component is created before the customer's addresses arrive, so the
    // radio always starts on "new" and it falls to this watcher to put it on
    // the right address once they land.
    //
    // FUPOF-191: a quote opened for editing already carries the shipping
    // address it was saved with. Re-select that saved address rather than
    // leaving the form on "+ Enter a new address" with the values sitting in
    // the manual-entry fields — which is what the customer sees as "it typed my
    // address into the new-address form instead of picking it".
    const saved = this.matchSavedAddress(addresses);
    if (saved) {
      this.selectedAddressId = saved.address_id;
      // Deliberately not applySelectedAddress(): those values are already on
      // the form, and re-applying would discard any per-quote edits made to
      // addressee/attention/label on top of the saved address.
      if (!this.freightAddressType) {
        this.freightAddressType = saved.is_residential
          ? ShippingAddressType.Residential
          : ShippingAddressType.Commercial;
        if (saved.is_residential) {
          this.freightDeliveryService = DeliveryServices.Both;
        }
        this.emitFreightInstructions();
      }
      return;
    }
    // Freight state already restored means a saved quote was loaded, and its
    // address is not one of the customer's — a genuine one-off. Leave it in the
    // new-address fields rather than overwriting it with the default.
    if (this.freightAddressType) return;
    const defaultAddr =
      addresses.find(a => a.is_default_shipping) ?? addresses[0];
    this.selectedAddressId = defaultAddr.address_id;
    this.applySelectedAddress(defaultAddr);
  }

  beforeDestroy() {
    EventBus.$off("submitCheckoutForm");
  }

  get zipMask(): string {
    let mask;
    if (this.country === "Canada") {
      mask = "A#A #A#";
    } else {
      mask = "#####";
    }
    return mask;
  }

  get states(): string[] {
    let list = ["--"];
    if (this.country === "Canada") {
      list = provinces;
    } else if (this.country === "US") {
      list = states;
    }
    return list;
  }

  get isFreightResidential(): boolean {
    return this.freightAddressType === ShippingAddressType.Residential;
  }

  get deliveryServiceOptions(): string[] {
    if (this.isFreightResidential) {
      return [DeliveryServices.Both];
    }
    return Object.values(DeliveryServices);
  }

  protected onFreightEstimateChange(): void {
    // Persist the Yes/No answer on its own field so it round-trips independent
    // of the freight_instructions string (which only emits once an address type
    // is set) and of address re-selection.
    this.$emit("update:request_freight_estimate", this.freightEstimate);
    if (this.freightEstimate === false) {
      this.freightDeliveryService = "";
    } else if (this.freightEstimate === true && this.isFreightResidential) {
      this.freightDeliveryService = DeliveryServices.Both;
    }
    // Always re-emit: the composed string carries the Yes/No answer too, and
    // only re-emitting on those two branches left a commercial address that
    // switched from No to Yes still saying "Freight Estimate - No." until a
    // delivery service happened to be picked.
    this.emitFreightInstructions();
    if (this.submitted) {
      this.$v.freightEstimate.$touch();
      // Toggling the estimate restructures the dynamic validations (the
      // delivery-service rule is added when Yes, removed when No). vuelidate
      // rebuilds that tree on the next tick, so reading $v.$invalid now would
      // emit a stale section error that sticks until a page refresh. Defer the
      // re-validation so the emitted state reflects the new answer.
      this.$nextTick(() => this.validateForm());
    }
  }

  protected onFreightAddressTypeChange(): void {
    if (this.isFreightResidential) {
      this.freightDeliveryService = DeliveryServices.Both;
    } else {
      this.freightDeliveryService = "";
    }
    if (this.submitted) {
      this.$v.freightAddressType.$touch();
    }
    this.emitFreightInstructions();
  }

  protected emitFreightInstructions(): void {
    if (this.freightAddressType) {
      let instructions = `Address Type - ${this.freightAddressType}.`;
      instructions += ` Freight Estimate - ${this.freightEstimate ? "Yes" : "No"}.`;
      if (this.freightEstimate === true && this.freightDeliveryService) {
        instructions += ` Services - ${this.freightDeliveryService}.`;
      }
      this.$emit("update:freight_instructions", instructions);
    }
    if (this.submitted) {
      this.$v.freightDeliveryService.$touch();
      this.validateForm();
    }
  }

  protected applySelectedAddress(addr: NetSuiteCustomerAddress): void {
    // Emit all field updates without triggering intermediate validations.
    // Props update asynchronously, so validating mid-batch sees stale values.
    this.$emit("update:label", addr.label ?? "");
    this.$emit("update:addressee", addr.addressee ?? "");
    this.$emit("update:attention", addr.attention ?? "");
    const newAddress: any = {
      address_1: addr.addr1 ?? "",
      address_2: addr.addr2 ?? "",
      address_3: addr.addr3 ?? "",
      city: addr.city ?? "",
      state: addr.state ?? "",
      postal_code: addr.zip ?? "",
      country: addr.country ?? "US"
    };
    this.$emit("update:address", newAddress);
    this.country = addr.country ?? "US";
    // Auto-populate address type from the saved NetSuite address
    this.freightAddressType = addr.is_residential
      ? ShippingAddressType.Residential
      : ShippingAddressType.Commercial;
    if (addr.is_residential) {
      this.freightDeliveryService = DeliveryServices.Both;
    } else {
      this.freightDeliveryService = "";
    }
    this.emitFreightInstructions();
    // Validate once after Vue has propagated the new prop values
    if (this.submitted) {
      this.$nextTick(() => {
        this.$v.$touch();
        this.validateForm();
      });
    }
  }

  protected onAddressSelect(addressId: number | "new"): void {
    this.selectedAddressId = addressId;
    if (addressId === "new") {
      this.updateItem("", "label");
      this.updateItem("", "addressee");
      this.updateItem("", "attention");
      this.updateAddress(
        {
          address_1: "",
          address_2: "",
          address_3: "",
          city: "",
          state: "",
          postal_code: ""
        },
        "address_1"
      );
      this.country = "US";
      this.freightAddressType = "";
      this.freightDeliveryService = "";
      this.$emit("update:freight_instructions", "");
    } else {
      const addr = this.addresses.find(a => a.address_id === addressId);
      if (addr) this.applySelectedAddress(addr);
    }
  }

  protected validateForm(): void {
    this.submitted = true;
    this.$v.$touch();
    this.$emit("sendError", this.$v.$invalid, "shipping");
  }

  /**
   * Have to explicitly call vuelidate ($v) methods if not using v-model
   */
  public updateItem(fieldValue: string, fieldName: string) {
    this.$emit(`update:${fieldName}`, fieldValue);
    if (this.submitted && this.$v[fieldName]) {
      this.$v[fieldName]!.$touch();
      this.validateForm();
    }
  }

  /**
   * Cannot update a nested property in an object due to the way Vue tracks nested data
   * i.e. update:address.address_1 = value will not trigger reactivity, so use Object.assign{} and update entire address object
   */
  public updateAddress(newObj: any, fieldName: string) {
    const newAddress = Object.assign(
      {},
      { ...this.address, country: this.country },
      { ...newObj }
    );
    this.$emit(`update:address`, newAddress);
    if (this.submitted && this.$v.address[fieldName]) {
      this.$v.address[fieldName]!.$touch();
      this.validateForm();
    }
  }
}
